// The scoping smoke resource — `/v1/organizations/{organizationId}/scope-probes` (INFRA-06, #31).
//
// It is to organisation scoping exactly what `GET /v1/hello` is to money: the smallest resource
// that exercises the whole chain end to end from the first milestone, so that M0's exit criterion —
// "RLS scoping is enforced and tested" (spec/22 §2) — is proved by something that runs rather than
// asserted in a README.
//
// It is deliberately a **write, a read and a list**, because those are the three ways one
// organisation could see another's row, and the acceptance criteria of #31 name all three. It is
// nested under the organisation because scope is explicit and comes from the path
// (spec/07 §6.1, spec/08 §2.7): there is no unscoped `/v1/scope-probes` and there never will be.
//
// Reading one that belongs to another organisation returns **404, not 403** — revealing that a row
// exists in an organisation you cannot see is the leak (spec/08 §2.6, spec/20 §11 rule 6).
//
// Like the rest of `meta`, nothing is ever built on top of it and it is deleted, not extended,
// when the modules that own real organisation data land.
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { ScopeProbeIdSchema, VenueIdSchema } from "../ids/schema.js";
import { IsoDateTimeSchema } from "../primitives/scalars.js";
import { PageQuerySchema, paginated } from "../primitives/page.js";
import { OrganizationScopeParamsSchema } from "../primitives/scope.js";
import { commonProblemResponses, jsonResponse, problemResponse } from "../openapi/responses.js";
export const ScopeProbeSchema = z
    .object({
    id: ScopeProbeIdSchema,
    /** Echoed back so a caller can see which organisation the server resolved from the path. */
    organizationId: z.string().openapi({ description: 'The organisation the row belongs to.' }),
    venueId: VenueIdSchema.nullable(),
    label: z.string().min(1).max(120),
    createdAt: IsoDateTimeSchema,
})
    .openapi('ScopeProbe', { description: 'One row of the organisation-scoped smoke resource.' });
export const ScopeProbeListSchema = paginated(ScopeProbeSchema, 'ScopeProbeList');
export const CreateScopeProbeSchema = z
    .object({
    label: z.string().min(1).max(120),
})
    // There is deliberately no `organizationId` and no `venueId` in the body. The organisation comes
    // from the path and the venue from `X-Clavian-Venue`, and the repository writes both onto the
    // row: a body that could name them would be a body that could choose whose row it writes.
    .openapi('CreateScopeProbe');
const ScopeProbeParamsSchema = OrganizationScopeParamsSchema.extend({
    scopeProbeId: ScopeProbeIdSchema,
}).openapi('ScopeProbeParams');
export const listScopeProbesRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/scope-probes',
    operationId: 'listScopeProbes',
    tags: ['meta'],
    summary: 'List the organisation scoping smoke rows',
    description: 'Returns only rows of the organisation in the path. The predicate is applied in code by the ' +
        'scoped repository and again by row-level security (spec/03 §7.1).',
    request: { params: OrganizationScopeParamsSchema, query: PageQuerySchema },
    responses: {
        200: jsonResponse(ScopeProbeListSchema, 'A page of rows, all of them this organisation’s.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        404: problemResponse('No such organisation, or it is not one you can see.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const getScopeProbeRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/scope-probes/{scopeProbeId}',
    operationId: 'getScopeProbe',
    tags: ['meta'],
    summary: 'Read one organisation scoping smoke row',
    description: 'A row of another organisation is 404, not 403: revealing that it exists is itself the leak ' +
        '(spec/08 §2.6, spec/20 §11 rule 6).',
    request: { params: ScopeProbeParamsSchema },
    responses: {
        200: jsonResponse(ScopeProbeSchema, 'The row.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        404: problemResponse('No such row in this organisation.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const createScopeProbeRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/scope-probes',
    operationId: 'createScopeProbe',
    tags: ['meta'],
    summary: 'Write an organisation scoping smoke row',
    description: 'The organisation is taken from the path and written onto the row by the repository; a body ' +
        'cannot choose it. `X-Clavian-Venue` narrows the scope to one venue.',
    request: {
        params: OrganizationScopeParamsSchema,
        body: { content: { 'application/json': { schema: CreateScopeProbeSchema } }, required: true },
    },
    responses: {
        201: jsonResponse(ScopeProbeSchema, 'The row that was written.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        404: problemResponse('No such organisation, or not one you can see.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
//# sourceMappingURL=scope-probe.js.map